import React, { useEffect, useRef } from 'react';
import {
    BarcodeFormat,
    BrowserMultiFormatReader,
    DecodeHintType,
} from '@zxing/library';

const NATIVE_FORMATS = [
    'ean_13',
    'ean_8',
    'upc_a',
    'upc_e',
    'code_128',
    'code_39',
    'itf',
];

const ZXING_HINTS = new Map<DecodeHintType, unknown>([
    [
        DecodeHintType.POSSIBLE_FORMATS,
        [
            BarcodeFormat.EAN_13,
            BarcodeFormat.EAN_8,
            BarcodeFormat.UPC_A,
            BarcodeFormat.UPC_E,
            BarcodeFormat.CODE_128,
            BarcodeFormat.CODE_39,
            BarcodeFormat.ITF,
        ],
    ],
]);

type BarcodeScannerProps = {
    open: boolean;
    setCode: (code: string) => void;
    setOpen: (open: boolean) => void;
    onError?: (error: unknown) => void;
};

export function BarcodeScanner({
    setCode,
    open,
    setOpen,
    onError,
}: BarcodeScannerProps) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const lastCodeRef = useRef<string | null>(null);
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    const setCodeRef = useRef(setCode);
    setCodeRef.current = setCode;
    const setOpenRef = useRef(setOpen);
    setOpenRef.current = setOpen;

    useEffect(() => {
        if (!open || !videoRef.current) return undefined;

        const video = videoRef.current;
        let stopped = false;
        let stream: MediaStream | null = null;
        let rafId: number | null = null;
        const reader = new BrowserMultiFormatReader(ZXING_HINTS);

        const emitCode = (code: string) => {
            if (lastCodeRef.current === code) return;
            lastCodeRef.current = code;
            setTimeout(() => {
                lastCodeRef.current = null;
            }, 1500);
            setCodeRef.current(code);
            setOpenRef.current(false);
        };

        // ZXing fallback: uses reader.decode() directly, avoiding internal camera management issues
        const startZXingLoop = () => {
            let lastAttempt = 0;
            const tick = (time: number) => {
                if (stopped) return;
                if (time - lastAttempt >= 50) {
                    lastAttempt = time;
                    try {
                        const result = reader.decode(video);
                        emitCode(result.getText());
                        return;
                    } catch {
                        // NotFoundException on frames without barcode — expected
                    }
                }
                rafId = requestAnimationFrame(tick);
            };
            rafId = requestAnimationFrame(tick);
        };

        // Native BarcodeDetector (Chrome/Edge): hardware-accelerated, most accurate
        const startNativeLoop = (detector: {
            detect(
                v: HTMLVideoElement,
            ): Promise<Array<{ rawValue: string; format: string }>>;
        }) => {
            let lastAttempt = 0;
            const schedule = () => {
                if (!stopped) rafId = requestAnimationFrame(tick);
            };
            const tick = async (time: number) => {
                if (stopped) return;
                if (time - lastAttempt >= 50) {
                    lastAttempt = time;
                    try {
                        if (video.readyState >= 2) {
                            const codes = await detector.detect(video);
                            const barcode = codes.find(
                                (c) =>
                                    c.rawValue &&
                                    NATIVE_FORMATS.includes(c.format),
                            );
                            if (barcode) {
                                emitCode(barcode.rawValue);
                                return;
                            }
                        }
                    } catch {
                        // detection failed on this frame — try the next one
                    }
                }
                schedule();
            };
            schedule();
        };

        (async () => {
            try {
                stream = await navigator.mediaDevices.getUserMedia({
                    audio: false,
                    video: {
                        facingMode: { ideal: 'environment' },
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                    },
                });

                if (stopped) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }

                video.srcObject = stream;
                await video.play();

                if (stopped) return;

                if ('BarcodeDetector' in window) {
                    try {
                        const detector = new (window as any).BarcodeDetector({
                            formats: NATIVE_FORMATS,
                        });
                        startNativeLoop(detector);
                    } catch {
                        // BarcodeDetector exists but threw (e.g. unsupported formats) — fall back to ZXing
                        startZXingLoop();
                    }
                } else {
                    startZXingLoop();
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('[BarcodeScanner] erro ao iniciar câmera:', err);
                if (!stopped) onErrorRef.current?.(err);
            }
        })();

        return () => {
            stopped = true;
            if (rafId !== null) cancelAnimationFrame(rafId);
            if (stream) stream.getTracks().forEach((t) => t.stop());
            video.srcObject = null;
        };
    }, [open]);

    return (
        <video
            ref={videoRef}
            muted
            playsInline
            style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
            }}
        />
    );
}

export default BarcodeScanner;
